import { useCallback, useEffect, useRef } from 'react';

export type RetriesHookIsActiveMethod = () => boolean;
export type RetriesHookStartMethod = () => void;
export type RetriesHookStopMethod = (triggerFinishCallback?: boolean) => void;

export type RetriesHookFinishCallback = () => void;
export type RetriesHookRetryCallback = (retries: number) => void;
export type RetriesHookErrorCallback = (error: any) => void;

export type RetriesHookOptions = {
  autoStart?: boolean;
  immediate?: boolean;
  retries?: number | undefined;
  onFinishRetries?: RetriesHookFinishCallback;
  onRetry?: RetriesHookRetryCallback;
  onError?: RetriesHookErrorCallback;
};

export type RetriesHookResult = {
  startRetriesTimer: (...args: any[]) => void;
  stopRetriesTimer: RetriesHookStopMethod;
  isRetriesTimerActive: RetriesHookIsActiveMethod;
};

export default function useRetryInterval(
  callback: (...args: any[]) => Promise<void>,
  delay: number,
  {
    autoStart = false,
    retries = 3,
    immediate = false,
    onRetry,
    onFinishRetries,
    onError
  }: RetriesHookOptions = {},
  ...args: any[]
): any[] {
  const savedCallback = useRef<(...args: any[]) => Promise<void>>();
  const count = useRef<number>(0);
  const timer = useRef<number | undefined>(undefined);
  const active = useRef<boolean>(false);
  const params = useRef<any[]>([...args]);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    autoStart && startRetriesTimer();

    return stopRetriesTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function tick() {
    count.current = count.current + 1;

    if (savedCallback.current) {
      onRetry && onRetry(count.current);
      try {
        await savedCallback.current(...params.current).then(() => {
          if (retries !== undefined && count.current === retries) {
            onFinishRetries && onFinishRetries();
            stopRetriesTimer();
          } else {
            setTimer(delay);
          }
        });
      } catch (error) {
        console.log(error);
        onError && onError(error);
        stopRetriesTimer();
      }
    }
  }

  const setTimer = (milliseconds: number) => {
    if (timer.current !== undefined) {
      clearTimeout(timer.current);
    }

    if (active.current) {
      timer.current = setTimeout(tick, milliseconds);
    } else {
      // eslint-disable-next-line no-console
      console.debug(
        'Trying to set interval timeout on inactive timer, this is no-op and probably indicates bug in your code.'
      );
    }
  };

  const startRetriesTimer = async (...args: any[]) => {
    const isActive = active.current;

    params.current = args;
    active.current = true;

    if (immediate && !isActive) {
      await tick();
    } else {
      setTimer(delay);
    }
  };

  const stopRetriesTimer = useCallback(
    (triggerFinishCallback = false) => {
      if (timer.current !== undefined) {
        clearTimeout(timer.current);
      }

      if (triggerFinishCallback && active.current) {
        onFinishRetries && onFinishRetries();
      }

      active.current = false;
      timer.current = undefined;
      count.current = 0;
    },
    [onFinishRetries]
  );

  const isRetriesTimerActive = useCallback(() => active.current, []);

  return [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive];
}
